import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Divider, FormControl, FormHelperText, IconButton, Input, InputBase, InputLabel, makeStyles, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HighlightOffSharpIcon from '@material-ui/icons/HighlightOffSharp';
import AssignmentIcon from '@material-ui/icons/Assignment';
import clsx from "clsx";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import RoleControl from "../RoleControl";
import { SUBJ_AIRPORT_GROUP_SETTING, VERB_CREATE, VERB_DELETE, VERB_UPDATE } from "../../utils/AccessControl";
import { useAirportGroupList, useAirportGroup, useCandidateAirportList } from "../../store/AirportGroupState";
import { accountSettingOperation, useAccountSettingEditing } from "../../store/AccountSettingEditingState";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		'&:nth-child(2)': {
			position: 'sticky',
			left: '34px',
			zIndex: 2,
			// fontWeight: 'bold',
			whiteSpace: 'nowrap',
		},
		verticalAlign: 'baseline',
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+2)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	ctrlCell: {
		width: '96px',
		minWidth: '96px',
		maxWidth: '96px',
		verticalAlign: 'middle',
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	containerBox: {
		flex: 1,
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	airportGroupBox: {
		paddingTop: 5,
		paddingLeft: 5,
		height: 62,
		"& .MuiFormHelperText-root.Mui-error": {
			marginTop: 0,
		},
	},
	textfield: {
		width: 300,
		"& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
			transform: "translate(10px, 13px) scale(1)",
		},
		"& .MuiInputBase-input": {
			height: 5,
		},
		"& .Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderWidth: 1,
		},
		"& .Mui-disabled .MuiInputBase-input": {
			height: 5,
		},
	},
	transferListBox: {
		marginBottom: 10,
		height: 350,
		display: "flex",
	},
	airportListBox: {
		position: 'relative',
		lineHeight: '15px',
		paddingLeft: '5px',
		width: '40%',
		"& .MuiInput-underline:hover:not(.Mui-disabled):before": {
			borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
		},
		"& .MuiInput-underline:before": {
			borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
		},
		"& .MuiInput-underline:after": {
			borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
		},
	},
	filterInputBox: {
		height: 50,
		position: "relative",
	},
	clearFilterIcon: {
		position: "absolute",
		right: 0,
	},
	airportList: {
		width: '100%',
		height: "calc(100% - 50px)",
		paddingTop: 8,
		paddingBottom: 7,
		"& .MuiSelect-root": {
			height: "100%",
			border: "1px solid rgba(0, 0, 0, 0.42)",
			borderBottom: "none",
		},
		"& ::-webkit-scrollbar": {
			width: "10px",
			height: "10px",
		},
		"& ::-webkit-scrollbar-thumb": {
			borderRadius: " 10px",
			backgroundColor: "rgba(0, 0, 50, .5)",
			border: "1px solid rgba(255, 255, 255, .3)",
		},
		"& ::-webkit-scrollbar-corner": {
			backgroundColor: "transparent",
		},
		"& .MuiSelect-select.MuiSelect-select": {
			paddingRight: 0,
		},
	},
	arrowBox: {
		alignSelf: "center",
	},
	rightArrowBtn: {
		"& .MuiIconButton-label": {
			color: "rgba(70, 70, 255, 0.75)",
		},
		"&.Mui-disabled .MuiIconButton-label": {
			color: "rgba(70, 70, 255, 0.25)",
		},
		"&:hover .MuiIconButton-label": {
			color: "rgb(70, 70, 255)",
		},
	},
	leftArrowBtn: {
		"& .MuiIconButton-label": {
			color: "rgba(255, 70, 70, 0.75)",
		},
		"&.Mui-disabled .MuiIconButton-label": {
			color: "rgba(255, 70, 70, 0.25)",
		},
		"&:hover .MuiIconButton-label": {
			color: "rgb(255, 70, 70)",
		},
	},
	divider: {
		width: "100%",
		backgroundColor: "rgba(0, 0, 0, 0.42)",
		// marginTop: 10,
		marginBottom: 5,
	},
	newline: {
		whiteSpace: "break-spaces",
	},
});

const ACTION = {
	NEW: "NEW",
	COPY: "COPY",
	EDIT: "EDIT",
	VIEW: "VIEW",
};

const columns = [
	{ id: 'airport_group', label: 'Airport Group' },
];

const AirportGroupListTable = ({filterWord, onEdit, onCopy, onView, setWaiting, tableRef}) => {
	const { airportGroupList, deleteAirportGroup, refreshAirportGroupList } = useAirportGroupList();
	const [del, setDel] = useState();
	const { showError } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const handleDelete = (row) => {
		setDel(row);
		setOpen(true);
	}

	const list = useCallback(() => {
		return airportGroupList
			.filter(v => {
				if (filterWord === "") {
					return true;
				} else {
					return v.indexOf(filterWord) !== -1;
				}
			})
			.map(v => {
				return {
					airport_group: v
				};
			});
	}, [airportGroupList, filterWord]);

	useEffect(() => {
		return () => {
			refreshAirportGroupList();
		}
	}, [refreshAirportGroupList]);

	// 確認ダイアログ
	const [open, setOpen] = useState(false);

	const handleDeleteOk = async () => {
		const _data = Object.assign({}, del);
		setOpen(false);
		setWaiting(true);
		try {
			const result = await deleteAirportGroup([_data.airport_group]);
			if (result && result.length > 0) {
				showError(`${_data.airport_group} \n ${t('Delete failed')}`);
			}
		} catch (e) {
			showError(`${_data.airport_group} \n ${t('Delete failed')}`);
		} finally {
			setWaiting(false);
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleKeyUp = async (e) => {
		if (e.keyCode === 13) {
			await handleDeleteOk();
		}
	};

	let detail = '';
	if (del) {
		detail = [
			"\n",
			`${t('Airport Group')}: ${del.airport_group}`
		].join('\n');
	}

	return (
		<>
			<TableContainer className={classes.container} ref={tableRef}>
				<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
					<TableHead>
						<TableRow className={classes.tableRow}>
							<TableCell
								key='ctrl'
								className={clsx(classes.headerCell, classes.tableCell, classes.ctrlCell)}
								padding="none"
							>
							</TableCell>
							{columns.map((column, i) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: 0 }}
									className={clsx(classes.headerCell, classes.tableCell)}
								>
									{t(column.label)}
								</TableCell>
							))}
							<TableCell
								key='delete'
								className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
								padding="none"
							>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{list().map((row) => {
							return (
								<TableRow hover role="checkbox" tabIndex={-1} key={row.airport_group}>
									<TableCell key='ctrlBtns' className={clsx(classes.tableCell, classes.ctrlCell)} padding="none" align="center">
										<IconButton aria-label="show" size="small" className={classes.smallBtn} onClick={() => onView(row)}>
											<Tooltip title="show" arrow >
												<AssignmentIcon fontSize="small" />
											</Tooltip>
										</IconButton>
										<RoleControl subject={SUBJ_AIRPORT_GROUP_SETTING} verb={VERB_UPDATE}>
											<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => onEdit(row)}>
												<Tooltip title="edit" arrow >
													<PencilIcon fontSize="small" />
												</Tooltip>
											</IconButton>
										</RoleControl>
										<RoleControl subject={SUBJ_AIRPORT_GROUP_SETTING} verb={VERB_UPDATE}>
											<IconButton aria-label="copy" size="small" className={classes.smallBtn} onClick={() => onCopy(row)}>
												<Tooltip title="copy" arrow >
													<FileCopyIcon fontSize="small" />
												</Tooltip>
											</IconButton>
										</RoleControl>
									</TableCell>
									{columns.map((column, i) => {
										const value = row[column.id];
										return (
											<TableCell
												key={column.id}
												align={column.align}
												className={classes.tableCell}
											>
												{value}
											</TableCell>
										);
									})}
									<TableCell key='deleteBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
										<RoleControl subject={SUBJ_AIRPORT_GROUP_SETTING} verb={VERB_DELETE}>
											<IconButton aria-label="delete" size="small" className={classes.smallBtn} onClick={() => handleDelete(row)}>
												<Tooltip title="delete" arrow >
													<DeleteIcon fontSize="small" />
												</Tooltip>
											</IconButton>
										</RoleControl>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
				<DialogContent>
					<DialogContentText className={classes.newline}>{t('AIRPORT_GROUP_DELETE_CONFIRM', {detail})}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">Cancel</Button>
					<Button onClick={handleDeleteOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

const AirportGroupPopup = ({action, formData, onClose, setWaiting}) => {
	const airportGroupName = formData?.airport_group || '';
	const { candidateAirportList } = useCandidateAirportList();
	const { airportGroupList, airportList, insertAirportGroup, updateAirportGroup, refreshAirportGroup } = useAirportGroup(airportGroupName);
	const { accountSettingEditing, startEditing, endEditing } = useAccountSettingEditing();

	useEffect(() => {
		return () => {
			refreshAirportGroup();
		}
	}, [refreshAirportGroup]);

	//空港グループ名
	const [ airportGroup, setAirportGroup ] = useState(action !== ACTION.COPY ? airportGroupName : 'Copy_of_' + airportGroupName);
	const airportGroupRef = useRef(null);
	//候補空港情報(左リスト)
	const [ candidate, setCandidate ] = useState(candidateAirportList.map(icao => {
		return {
			icao,
			selected: false,
			visible: !airportList.includes(icao),
			filtered: false,
		}
	}));
	//対象空港情報(右リスト)
	const [target, setTarget] = useState(airportList.map(icao => {
		return {
			icao, 
			selected: false,
			visible: true,
			filtered: false,
			initial: true,
		}
	}));
	//候補空港フィルター
	const [candidateFilter, setCandidateFilter] = useState('');
	const candidateFilterRef = useRef(null);
	//対象空港フィルター
	const [targetFilter, setTargetFilter] = useState('');
	const targetFilterRef = useRef(null);

	//空港グループ名重複
	const duplicate = action !== ACTION.VIEW && action !== ACTION.EDIT && airportGroupList.includes(airportGroup);
	//ボタン状態
	const clearCandidateFilterBtnDisabled = candidateFilter === '';
	const clearTargetFilterBtnDisabled = targetFilter === '';
	const moveBtnDisabled = action === ACTION.VIEW || candidate.filter(data => data.selected).length === 0;
	const removeBtnDisabled = action === ACTION.VIEW || target.filter(data => data.selected).length === 0;
	let saveBtnDisabled = true;
	if (airportGroup !== '' && !duplicate) {
		if (action === ACTION.VIEW) {
			saveBtnDisabled = true;
		} else if (action !== ACTION.EDIT) {
			saveBtnDisabled = target.filter(data => data.visible).length === 0;
		} else {
			const changedCount = target.filter(data => data.visible !== data.initial).length;
			saveBtnDisabled = changedCount === 0 || target.filter(data => data.visible).length === 0;
		}
	}

	const classes = useStyles();
	const {t} = useTranslation();
	const {showError} = useAlert();

	//編集中判定
	const checkEditing = (_airportGroup, _target) => {
		let editing = false;
		if (action === ACTION.VIEW) {
			editing = false;
		} else if (action === ACTION.EDIT) {
			editing = _target.filter(data => data.visible !== data.initial).length > 0;
		} else {
			editing = _airportGroup !== '' || _target.filter(data => data.visible).length > 0;
		}

		if (accountSettingEditing.editing !== editing) {
			if (accountSettingEditing.editing) {
				endEditing();
			} else {
				startEditing(accountSettingOperation.AIRPORT_GROUP_SETTING);
			}
		}
	};

	//空港グループ名編集
	const handleAirportGroupChange = () => {
		if (airportGroupRef.current) {
			const ref = airportGroupRef.current;
			if (!ref.validity.valid) {
				return;
			}
			const input = ref.value;
			setAirportGroup(input);
			checkEditing(input, target);
		}
	};

	//候補空港or対象空港のフィルター
	const filterList = (input, list, otherList) => {
		const otherIcaoList = otherList.filter(data => data.visible).map(data => data.icao);
		const changed = [];
		list.forEach(data => {
			const visible = !otherIcaoList.includes(data.icao);
			let selected = false;
			let filtered = false;
			if (visible && input !== '') {
				const match = data.icao.startsWith(input);
				if (match) {
					selected = true;
				} else {
					filtered = true;
				}
			}
			changed.push({...data, selected, visible, filtered});
		});
		return changed;
	};

	//候補空港フィルターor対象空港フィルターEnterで「→」「←」ボタン押下時の処理を呼び出し
	const handleKeyDown = (event) => {
		if(event.keyCode === 13) {
			if (event.target.id === 'candidate-filter' && !moveBtnDisabled) {
				handleMove();
			} else if (event.target.id === 'target-filter' && !removeBtnDisabled) {
				handleRemove();
			}
		}
	};

	//候補空港フィルターor対象空港フィルタークリア
	const handleClearFilter = (event) => {
		if (event.currentTarget.id === 'clear-candidate-filter' && !clearCandidateFilterBtnDisabled) {
			filterCandidateList("");
		} else if (event.currentTarget.id === 'clear-target-filter' && !clearTargetFilterBtnDisabled) {
			filterTargetList("");
		}
	};

	//候補空港フィルター編集
	const handleInputCandidateFilter = () => {
		if (candidateFilterRef.current) {
			const ref = candidateFilterRef.current;
			if (!ref.validity.valid) {
				return;
			}
			filterCandidateList(ref.value.toUpperCase());
		}
	};
	const filterCandidateList = (input) => {
		setCandidateFilter(input);
		const result = filterList(input, candidate, target);
		setCandidate(result);
	};

	//候補空港リスト選択
	const handleCandidateSelect = event => {
		const selected = [];
		for (let opt of event.target.selectedOptions) {
			selected.push(opt.value);
		}
		const changedCandidate = [];
		candidate.forEach(data => {
			changedCandidate.push({...data, selected: selected.includes(data.icao)});
		});
		setCandidate(changedCandidate);
	};

	//対象空港フィルター編集
	const handleInputTargetFilter = () => {
		if (targetFilterRef.current) {
			const ref = targetFilterRef.current;
			if (!ref.validity.valid) {
				return;
			}
			filterTargetList(ref.value.toUpperCase());
		}
	};
	const filterTargetList = (input) => {
		setTargetFilter(input);
		const result = filterList(input, target, candidate);
		setTarget(result);
	};

	//対象空港リスト選択
	const handleTargetSelect = event => {
		const selected = [];
		for (let opt of event.target.selectedOptions) {
			selected.push(opt.value);
		}
		const changedTarget = [];
		target.forEach(data => {
			changedTarget.push({...data, selected: selected.includes(data.icao)});
		});
		setTarget(changedTarget);
	};

	//「→」ボタン押下
	const handleMove = () => {
		const candidateToTargetList = candidate.filter(data => data.selected).map(data => data.icao);
		const changedTarget = [];
		target.forEach(data => {
			const index = candidateToTargetList.indexOf(data.icao);
			if (index > -1) {
				changedTarget.push({ ...data, selected: false, visible: true });
				candidateToTargetList.splice(index, 1);
			} else {
				changedTarget.push({ ...data });
			}
		});
		candidateToTargetList.forEach(icao => {
			changedTarget.push({ icao: icao, selected: false, initial: false, visible: true });
		});
		changedTarget.sort((a, b) => {
			return a.icao > b.icao ? 1 : -1;
		});

		const resultCandidate = filterList(candidateFilter, candidate, changedTarget);
		setCandidate(resultCandidate);
		const resultTarget = filterList(targetFilter, changedTarget, resultCandidate);
		setTarget(resultTarget);
		checkEditing(airportGroup, resultTarget);
	};

	//「←」ボタン押下
	const handleRemove = () => {
		const changedTarget = [];
		const targetToCandidateList = [];
		target.forEach(data => {
			if (data.selected) {
				if (data.initial) {
					changedTarget.push({ ...data, selected: false, visible: false });
				}
				targetToCandidateList.push(data.icao);
			} else {
				changedTarget.push({ ...data });
			}
		});

		const changedCandidate = [];
		candidate.forEach(data => {
			if (targetToCandidateList.includes(data.icao)) {
				changedCandidate.push({ ...data, selected: false, visible: true });
			} else {
				changedCandidate.push({ ...data });
			}
		});

		const resultTarget = filterList(targetFilter, changedTarget, changedCandidate);
		setTarget(resultTarget);
		const resultCandidate = filterList(candidateFilter, changedCandidate, resultTarget);
		setCandidate(resultCandidate);
		checkEditing(airportGroup, resultTarget);
	};

	//CANCELボタン押下
	const handleCancel = () => {
		endEditing();
		onClose();
	};

	//Saveボタン押下
	const handleSave = async () => {
		setWaiting(true);
		try {
			const newAirportList = target.filter(data => data.visible).map(data => data.icao);

			//空港グループ設定API呼び出し
			let result;
			if (action === ACTION.EDIT) {
				result = await updateAirportGroup(newAirportList);
			} else {
				result = await insertAirportGroup(airportGroup, newAirportList);
			}
			if (result) {
				//成功時、編集中状態を解除して空港グループ一覧画面に戻る
				endEditing();
				onClose();
			} else {
				showError(`${airportGroup} \n ${t('Apply failed')}`);
			}
		} catch (e) {
			showError(`${airportGroup} \n ${t('Apply failed')}`);
		} finally {
			setWaiting(false);
		}
	};

	return (
		<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
			<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
				<Box style={{ marginLeft: 'auto'}}>
					<IconButton aria-label="close" className={classes.closeButton} onClick={handleCancel}>
						<CloseIcon />
					</IconButton>
				</Box>
				<Box>
					<Button variant="outlined" size="small" onClick={handleSave} disabled={saveBtnDisabled}>{t('save')}</Button>
				</Box>
			</Box>
			<Box style={{ display: 'flex', flexDirection: 'column' }}>
				<Box className={classes.airportGroupBox}>
					<FormControl error={duplicate}>
						<TextField
							variant="outlined"
							className={classes.textfield}
							label={t('Airport Group')}
							value={airportGroup}
							onChange={handleAirportGroupChange}
							fullWidth
							autoFocus={action !== ACTION.EDIT}
							disabled={action === ACTION.EDIT || action === ACTION.VIEW}
							inputProps={{
								pattern: "^[a-zA-Z0-9_@-]+$",
							}}
							inputRef={airportGroupRef}
							InputLabelProps={{
								shrink: true
							}}
							aria-describedby="airport-group-text-error"
						/>
						{!duplicate && (
            			<FormHelperText id="airport-group-helper-text" style={{ marginTop: 0 }}>
                		{t('Alphanumeric characters, _, @, and - are allowed.')}</FormHelperText>)}
						{duplicate && <FormHelperText id="airport-group-text-error">{t('already exists')}</FormHelperText>}
					</FormControl>
				</Box>
				<Divider className={classes.divider}></Divider>
				<Box className={classes.transferListBox}>
					<Box className={classes.airportListBox}>
						<Box className={classes.filterInputBox}>
							<InputLabel shrink htmlFor="candidate-airport-select">{t('candidate airport')}</InputLabel>
							<Input
								id="candidate-filter"
								type="text"
								value={candidateFilter}
								autoFocus={action === ACTION.EDIT}
								onChange={handleInputCandidateFilter}
								onKeyDown={handleKeyDown}
								inputProps={{
									maxLength: 4,
									pattern: "^[a-zA-Z]+$",
								}}
								inputRef={candidateFilterRef}
								placeholder={t('filter by ICAO')}
							/>
							<IconButton id="clear-candidate-filter" size='small' className={classes.clearFilterIcon} onClick={handleClearFilter} disabled={clearCandidateFilterBtnDisabled}>
								<HighlightOffSharpIcon color={'inherit'} />
							</IconButton>
						</Box>
						<Select
							className={classes.airportList}
							multiple
							native
							value={candidate.filter(data => data.visible && !data.filtered && data.selected).map(data => data.icao)}
							onChange={handleCandidateSelect}
							inputProps={{
								id: 'candidate-airport-select',
							}}
						>
							{candidate.filter(data => data.visible && !data.filtered).map((data) => (
								<option key={data.icao} value={data.icao}>{data.icao}</option>
							))}
						</Select>
					</Box>
					<Box className={classes.arrowBox}>
						<Box>
							<IconButton className={classes.rightArrowBtn} onClick={handleMove} disabled={moveBtnDisabled}>
								<ArrowForwardIcon color={'inherit'} />
							</IconButton>
						</Box>
						<Box>
							<IconButton className={classes.leftArrowBtn} onClick={handleRemove} disabled={removeBtnDisabled}>
								<ArrowBackIcon color={'inherit'} />
							</IconButton>
						</Box>
					</Box>
					<Box className={classes.airportListBox}>
						<Box className={classes.filterInputBox}>
							<InputLabel shrink htmlFor="target-airport-select">{t('target airport')}</InputLabel>
							<Input
								id="target-filter"
								type="text"
								value={targetFilter}
								onChange={handleInputTargetFilter}
								onKeyDown={handleKeyDown}
								inputProps={{
									maxLength: 4,
									pattern: "^[a-zA-Z]+$",
								}}
								inputRef={targetFilterRef}
								placeholder={t('filter by ICAO')}
							/>
							<IconButton id="clear-target-filter" size='small' className={classes.clearFilterIcon} onClick={handleClearFilter} disabled={clearTargetFilterBtnDisabled}>
								<HighlightOffSharpIcon color={'inherit'} />
							</IconButton>
						</Box>
						<Select
							className={classes.airportList}
							multiple
							native
							value={target.filter(data => data.visible && !data.filtered && data.selected).map(data => data.icao)}
							onChange={handleTargetSelect}
							inputProps={{
								id: 'target-airport-select',
							}}
						>
							{target.filter(data => data.visible && !data.filtered).map((data) => (
								<option key={data.icao} value={data.icao}>{data.icao}</option>
							))}
						</Select>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

function AirportGroupSetting() {
	const tableRef = useRef(null);
	const inputFilterRef = useRef(null);
	const [formData, setFormData] = useState({});
	const [filterWord, setFileterWord] = useState('');
	const [open, setOpen] = useState(false);
	const [action, setAction] = useState('');
	const [waiting, setWaiting] = useState(false);

	const classes = useStyles();
	const {t} = useTranslation();

	const handleNew = () => {
		setAction(ACTION.NEW);
		// reset(formDefault);
		setFormData({});
		setOpen(true);
	}

	const handleEdit = (row) => {
		setAction(ACTION.EDIT);
		const _formData = Object.assign({}, row);
		setFormData(_formData);
		setOpen(true);
	}

	const handleCopy = (row) => {
		setAction(ACTION.COPY);
		const _formData = Object.assign({}, row);
		setFormData(_formData);
		setOpen(true);
	}

	const handleView = (row) => {
		setAction(ACTION.VIEW);
		const _formData = Object.assign({}, row);
		setFormData(_formData);
		setOpen(true);
	}

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleClose = () => {
		setOpen(false);
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box className={classes.containerBox}>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
					<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(); event.preventDefault(); }}>
						<div className={classes.searchIcon}>
							<SearchIcon />
						</div>
						<InputBase
							className={classes.input}
							placeholder={`${t('Airport Group')}`}
							inputProps={{ 'aria-label': 'filter airport group list' }}
							inputRef={inputFilterRef}
							defaultValue={filterWord}
							// onChange={handleChange}
						/>
						{filterWord.length > 0 &&
						<IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
							<CloseIcon />
						</IconButton>}
					</Paper>
					<RoleControl subject={SUBJ_AIRPORT_GROUP_SETTING} verb={VERB_CREATE}>
						<Button variant="outlined" size="small" onClick={handleNew}>{t('New')}</Button>
					</RoleControl>
				</Box>
				<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
					{waiting && <Loading backdrop={true} />}
					<Suspense fallback={<Loading />}>
						<AirportGroupListTable filterWord={filterWord} onEdit={handleEdit} onCopy={handleCopy} onView={handleView} setWaiting={setWaiting} tableRef={tableRef} />
					</Suspense>
				</Box>
			</Box>
			<Suspense fallback={<Loading/>}>
				<Dialog maxWidth="md" onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
					<DialogContent>
						<AirportGroupPopup action={action} formData={formData} onClose={handleClose} setWaiting={setWaiting}/>
					</DialogContent>
				</Dialog>
			</Suspense>
		</Box>
	)
}

export default AirportGroupSetting;
